import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import API_BASE_URL from "../config/api";
import { getSessionToken } from "../auth/sessionToken";
import { useAuth } from "../auth/AuthContext";

const ChatContext = createContext();

export const useChat = () => useContext(ChatContext);

export const getDefaultAvatar = (name) => {
    if (!name) return { initials: "?", color: "#94a3b8" };

    const colors = [
        "#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6",
        "#ec4899", "#14b8a6", "#f97316", "#06b6d4", "#6366f1"
    ];

    const nameParts = name.trim().split(" ");
    const initials = nameParts.length > 1
        ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
        : name.substring(0, 2).toUpperCase();

    const charSum = name.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
    const color = colors[charSum % colors.length];

    return { initials, color };
};

const PING_INTERVAL_MS = 30_000;
const RECONNECT_BASE_MS = 2_000;
const RECONNECT_MAX_MS = 30_000;

export const ChatProvider = ({ children }) => {
    const { user, isAuthenticated } = useAuth();

    const [chats, setChats] = useState([]);
    const [activeChat, setActiveChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoadedChats, setHasLoadedChats] = useState(false);
    const [isChatbotOpen, setIsChatbotOpen] = useState(false);
    const [chatProductContext, setChatProductContext] = useState({});

    const activeChatRef = useRef(null);
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const pingIntervalRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const messagesEndRef = useRef(null);
    const hasLoadedChatsRef = useRef(false);

    const getToken = getSessionToken;

    const getUserId = () => {
        if (user) return String(user.id || user._id || user.user_id || "");
        const userId = localStorage.getItem("user_id");
        if (userId) return userId;
        try {
            const userStr = localStorage.getItem("user");
            if (userStr) {
                const u = JSON.parse(userStr);
                return u.id || u._id || u.user_id;
            }
        } catch (e) { }
        return null;
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Update chat list in-place from a WS message payload — no API call needed.
    const applyIncomingMessage = useCallback((chatId, message) => {
        setChats(prev => {
            const idx = prev.findIndex(c => c.chatId === chatId);
            if (idx === -1) return prev;
            const updated = [...prev];
            updated[idx] = {
                ...updated[idx],
                lastMessage: {
                    content: message.content,
                    senderId: message.senderId,
                    timestamp: message.timestamp,
                },
                unreadCount: activeChatRef.current?.chatId === chatId
                    ? 0
                    : (updated[idx].unreadCount || 0) + 1,
            };
            // Bubble the updated chat to the top
            const [chat] = updated.splice(idx, 1);
            return [chat, ...updated];
        });
    }, []);

    const connectWebSocket = useCallback(async (userId) => {
        const token = await getToken();
        if (!token || !userId) return;

        if (wsRef.current) {
            wsRef.current.close();
        }
        clearInterval(pingIntervalRef.current);

        const wsUrl = API_BASE_URL.replace(/^http/, 'ws') + `/ws/chat?token=${token}`;
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
            reconnectAttemptsRef.current = 0;
            // Keep-alive ping every 30s
            pingIntervalRef.current = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({ type: 'ping' }));
                }
            }, PING_INTERVAL_MS);
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.type === 'pong') return;

                if (data.type === 'new_message') {
                    const { chatId, message } = data;

                    if (activeChatRef.current?.chatId === chatId) {
                        setMessages(prev => {
                            if (prev.some(m => m.id === message.id)) return prev;
                            return [...prev, message];
                        });
                        setTimeout(scrollToBottom, 50);
                    }

                    // Update chat list without a round-trip API call
                    applyIncomingMessage(chatId, message);
                }
            } catch (e) {
                console.error("WebSocket message error:", e);
            }
        };

        ws.onerror = () => { };

        ws.onclose = () => {
            if (wsRef.current !== ws) return;
            wsRef.current = null;
            clearInterval(pingIntervalRef.current);

            // Exponential backoff: 2s, 4s, 8s … capped at 30s
            const attempts = reconnectAttemptsRef.current;
            const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempts, RECONNECT_MAX_MS);
            reconnectAttemptsRef.current = attempts + 1;

            reconnectTimeoutRef.current = setTimeout(() => {
                const currentUserId = getUserId();
                if (currentUserId) {
                    connectWebSocket(currentUserId);
                }
            }, delay);
        };

        wsRef.current = ws;
    }, [applyIncomingMessage]);

    const refreshChats = useCallback(async () => {
        const token = await getToken();
        if (!token) return;

        try {
            if (!hasLoadedChatsRef.current) setIsLoading(true);

            const res = await fetch(`${API_BASE_URL}/api/chats/`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                setChats(data);
                if (!hasLoadedChatsRef.current) {
                    setHasLoadedChats(true);
                    hasLoadedChatsRef.current = true;
                }
            }
        } catch (e) {
            console.error("Error fetching chats:", e);
        } finally {
            if (!hasLoadedChatsRef.current) setIsLoading(false);
        }
    }, []);

    const selectChat = useCallback(async (chat) => {
        if (!chat) {
            setActiveChat(null);
            activeChatRef.current = null;
            setMessages([]);
            return;
        }

        setActiveChat(chat);
        activeChatRef.current = chat;

        // Reset unread badge immediately in the list
        setChats(prev => prev.map(c =>
            c.chatId === chat.chatId ? { ...c, unreadCount: 0 } : c
        ));

        const token = await getToken();
        if (!token) return;

        try {
            const res = await fetch(`${API_BASE_URL}/api/chats/${chat.chatId}/messages?limit=100`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                setMessages(data);
            }
        } catch (e) {
            console.error("Error fetching messages:", e);
        }
    }, []);

    const sendMessage = async (content) => {
        const currentChat = activeChatRef.current;
        if (!currentChat) return;

        const token = await getToken();
        if (!token) return;

        const userId = getUserId();
        const tempId = `temp-${Date.now()}`;

        const tempMessage = {
            id: tempId,
            senderId: userId,
            content,
            timestamp: new Date().toISOString(),
            isRead: false
        };

        setMessages(prev => [...prev, tempMessage]);

        try {
            const res = await fetch(`${API_BASE_URL}/api/chats/${currentChat.chatId}/messages`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    recipientId: currentChat.otherUser.id,
                    content
                })
            });

            if (res.ok) {
                const data = await res.json();
                // Swap temp message with the confirmed one from server
                setMessages(prev => {
                    if (prev.some(m => m.id === data.message.id)) {
                        // WS already delivered the real message — just drop the temp
                        return prev.filter(m => m.id !== tempId);
                    }
                    return prev.map(m => m.id === tempId ? data.message : m);
                });
                // Update chat list last-message preview
                applyIncomingMessage(currentChat.chatId, data.message);
            } else {
                // Remove only this failed temp message
                setMessages(prev => prev.filter(m => m.id !== tempId));
            }
        } catch (e) {
            console.error("Error sending message:", e);
            setMessages(prev => prev.filter(m => m.id !== tempId));
        }
    };

    // Trim full product object down to only what the chat product card needs.
    // Prevents multi-KB payloads (media arrays, attributes, etc.) being stored
    // in every chat message's metadata.
    const slimProductData = (p) => {
        if (!p) return null;
        const firstImage =
            p.thumbnail ||
            p.media?.images?.[0]?.secure_url ||
            p.media?.images?.[0]?.url ||
            (Array.isArray(p.media) ? p.media[0]?.url : null) ||
            null;
        return {
            id: p.id || p._id,
            title: p.title,
            thumbnail: typeof firstImage === "string" ? firstImage : firstImage?.url || null,
            price: p.rental?.price_per_day || p.rental_details?.price_per_day || p.price || null,
            currency: p.rental?.currency || p.rental_details?.currency || "INR",
            category: p.category || null,
            city: p.location?.city || null,
        };
    };

    const initiateChat = async (recipientId, productData = null) => {
        const token = await getToken();
        if (!token) return null;

        let finalProductData = productData ? slimProductData(productData) : null;
        let existingChatId = null;

        if (productData) {
            const existingChat = chats.find(c => {
                const otherId = c.otherUser?.id || c.otherUser?._id;
                return String(otherId) === String(recipientId);
            });
            if (existingChat) {
                existingChatId = existingChat.chatId;
                try {
                    const messagesRes = await fetch(`${API_BASE_URL}/api/chats/${existingChat.chatId}/messages?limit=100&t=${Date.now()}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Cache-Control': 'no-cache, no-store, must-revalidate',
                            'Pragma': 'no-cache',
                            'Expires': '0'
                        }
                    });
                    if (messagesRes.ok) {
                        const existingMessages = await messagesRes.json();
                        const currentProdId = String(productData.id || productData._id || "");

                        // Find the very latest product card in history (regardless of which product it is)
                        const allProductCards = existingMessages.filter(m => m.metadata?.type === 'product_card' && m.metadata?.product);
                        const latestCard = allProductCards[allProductCards.length - 1];

                        if (latestCard) {
                            const latestCardProdId = String(latestCard.metadata.product.id || latestCard.metadata.product._id || "");

                            if (latestCardProdId !== currentProdId) {
                                // Requesting a new/different product (or switching back to first one) than the latest card in the chat
                                finalProductData = productData;
                            } else {
                                // Requesting the same product as the latest card. Check 24-hour limit.
                                const cardTime = new Date(
                                    latestCard.timestamp || latestCard.created_at || latestCard.createdAt || 0
                                ).getTime();
                                const oneDayInMs = 24 * 60 * 60 * 1000;

                                if (Date.now() - cardTime > oneDayInMs) {
                                    finalProductData = productData;
                                } else {
                                    finalProductData = null;
                                }
                            }
                        } else {
                            // No product card has ever been sent in this chat, send one
                            finalProductData = productData;
                        }
                    }
                } catch (err) {
                    console.error("Error checking existing chat messages for duplicate product:", err);
                }
            }
        }

        try {
            const res = await fetch(`${API_BASE_URL}/api/chats/initiate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ recipientId, productData: finalProductData })
            });

            if (res.ok) {
                const chat = await res.json();
                await refreshChats();

                const chatId = chat?.chatId || existingChatId;

                if (chat && chat.chatId && chat.otherUser) {
                    await selectChat(chat);
                }

                if (productData && chatId) {
                    setChatProductContext(prev => ({ ...prev, [chatId]: productData }));
                }
                return chat;
            }
        } catch (e) {
            console.error("Error initiating chat:", e);
        }
        return null;
    };

    useEffect(() => {
        if (isAuthenticated && user) {
            const userId = String(user.id || user._id || user.user_id || "");
            if (!userId) return;

            refreshChats();
            connectWebSocket(userId);
        } else if (!isAuthenticated && !user) {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            clearTimeout(reconnectTimeoutRef.current);
            clearInterval(pingIntervalRef.current);
            reconnectAttemptsRef.current = 0;
            activeChatRef.current = null;
            setChats([]);
            setMessages([]);
            setActiveChat(null);
            setHasLoadedChats(false);
            hasLoadedChatsRef.current = false;
            setIsLoading(false);
        }

        return () => {
            if (wsRef.current) wsRef.current.close();
            clearTimeout(reconnectTimeoutRef.current);
            clearInterval(pingIntervalRef.current);
        };
    }, [isAuthenticated, user, connectWebSocket, refreshChats]);

    const startPolling = useCallback(() => { }, []);
    const stopPolling = useCallback(() => { }, []);

    return (
        <ChatContext.Provider
            value={{
                chats,
                activeChat,
                messages,
                isLoading,
                hasLoadedChats,
                selectChat,
                sendMessage,
                initiateChat,
                refreshChats,
                messagesEndRef,
                startPolling,
                stopPolling,
                isChatbotOpen,
                setIsChatbotOpen,
                chatProductContext
            }}
        >
            {children}
        </ChatContext.Provider>
    );
};
